/**
 * API Service for Notification Topics.
 * Topics are merchant-scoped.
 *
 * Backend: Alaric.Services.Messaging - NotificationTopicsController
 * Base route: /api/v1/topics
 */

import { Result } from "../../../models/Result";
import {
    NotificationTopicDto,
    CreateTopicRequest,
    UpdateTopicRequest,
} from "../../../models/notifications";
import { messagingHttpService } from "./MessagingHttpService";

const BASE_PATH = "/api/v1/topics";

class TopicsApiService {
    /**
     * Get all active topics for a merchant (system + custom).
     */
    getAll = async (merchantId: number): Promise<Result<NotificationTopicDto[]>> => {
        return messagingHttpService.get<NotificationTopicDto[]>(
            `${BASE_PATH}?merchantId=${merchantId}`
        );
    };

    /**
     * Get a specific topic by ID.
     */
    getById = async (merchantId: number, topicId: string): Promise<Result<NotificationTopicDto>> => {
        return messagingHttpService.get<NotificationTopicDto>(
            `${BASE_PATH}/${topicId}?merchantId=${merchantId}`
        );
    };

    /**
     * Create a new custom topic.
     */
    create = async (request: CreateTopicRequest): Promise<Result<{ topicId: string; topicCode: string }>> => {
        return messagingHttpService.post<{ topicId: string; topicCode: string }>(BASE_PATH, request);
    };

    /**
     * Update a topic. System topics: only displayName and description can be changed.
     */
    update = async (merchantId: number, topicId: string, request: UpdateTopicRequest): Promise<Result<void>> => {
        return messagingHttpService.put<void>(
            `${BASE_PATH}/${topicId}?merchantId=${merchantId}`,
            request
        );
    };

    /**
     * Soft-delete a topic. System topics cannot be deleted.
     */
    delete = async (merchantId: number, topicId: string, deletedBy?: string): Promise<Result<void>> => {
        const params = new URLSearchParams();
        params.append("merchantId", merchantId.toString());
        if (deletedBy) params.append("deletedBy", deletedBy);
        return messagingHttpService.delete<void>(
            `${BASE_PATH}/${topicId}?${params.toString()}`
        );
    };
}

const topicsApiService = new TopicsApiService();

export { topicsApiService };
